import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, MessageCircle, Send, Clock } from "lucide-react";
import toast from "react-hot-toast";
import Navbar from "../../components/Navbar";

type Topic = "general" | "support" | "partnerships" | "press";

const topics: { value: Topic; label: string; hint: string }[] = [
  { value: "general", label: "General", hint: "Anything else on your mind." },
  { value: "support", label: "Support", hint: "Help with your account or a match." },
  { value: "partnerships", label: "Partnerships", hint: "Sponsorships, tournaments, clubs." },
  { value: "press", label: "Press", hint: "Interviews, quotes, brand assets." },
];

const channels = [
  {
    icon: Mail,
    label: "Email",
    value: "hello@sportofolo.com",
    href: "mailto:hello@sportofolo.com",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+91 80 0000 0000",
    href: "tel:+918000000000",
  },
  {
    icon: MapPin,
    label: "Office",
    value: "Pune, Maharashtra, India",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Mon–Sat · 10:00 – 19:00 IST",
  },
];

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "general" as Topic,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // Placeholder — no backend endpoint yet. Hand the message off cleanly.
    await new Promise((r) => setTimeout(r, 600));
    toast.success("Thanks! We'll get back to you within 2 business days.");
    setForm({ name: "", email: "", topic: "general", message: "" });
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(56,189,248,0.18),_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16 sm:pt-32 sm:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-sky-300 font-semibold tracking-wider text-xs sm:text-sm mb-4">
              GET IN TOUCH
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              Let's{" "}
              <span className="bg-gradient-to-r from-sky-400 to-emerald-400 bg-clip-text text-transparent">
                talk shop
              </span>
              .
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-2xl">
              Questions, ideas, partnerships, press — drop us a line. We read
              everything, and we reply fast.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== CONTENT ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          {/* Form */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                Send us a message
              </h2>
              <p className="text-gray-600 mb-8">
                Pick a topic so the right person picks it up.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Jane Player"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    {topics.map((t) => {
                      const active = form.topic === t.value;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => setForm({ ...form, topic: t.value })}
                          className={`text-left px-4 py-3 rounded-xl border-2 transition ${
                            active
                              ? "border-indigo-600 bg-indigo-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div
                            className={`font-semibold text-sm ${
                              active ? "text-indigo-700" : "text-gray-900"
                            }`}
                          >
                            {t.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {t.hint}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us a bit about what you need..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none transition"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-8 py-3.5 rounded-xl font-semibold hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  {submitting ? "Sending..." : "Send message"}
                </button>
              </form>
            </div>
          </div>

          {/* Channels */}
          <aside className="lg:col-span-2 order-1 lg:order-2 space-y-4">
            <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white rounded-3xl p-6 sm:p-8">
              <MessageCircle size={28} className="text-indigo-300 mb-3" />
              <h3 className="text-xl font-bold mb-1">Other ways to reach us</h3>
              <p className="text-gray-400 text-sm">
                Same team, different doors.
              </p>
            </div>
            {channels.map((c) => {
              const Icon = c.icon;
              const content = (
                <div className="flex items-start gap-4 p-5 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-sky-500 text-white flex items-center justify-center flex-shrink-0">
                    <Icon size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {c.label}
                    </div>
                    <div className="text-gray-900 font-medium mt-0.5 break-words">
                      {c.value}
                    </div>
                  </div>
                </div>
              );
              return c.href ? (
                <a key={c.label} href={c.href} className="block">
                  {content}
                </a>
              ) : (
                <div key={c.label}>{content}</div>
              );
            })}
          </aside>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16 sm:py-20 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Looking for a tournament instead?
          </h2>
          <p className="text-gray-600 mb-6">
            Browse what's live across cities, or join the community to host
            your own.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/tournaments"
              className="bg-slate-900 text-white px-8 py-3 rounded-full font-semibold hover:bg-slate-800 transition"
            >
              Browse Tournaments
            </Link>
            <Link
              to="/signup"
              className="border border-gray-300 text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-50 transition"
            >
              Join Sportofolo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
